"use client"

import Image from "next/image"
import { motion, useMotionValue, useSpring, useTransform } from "motion/react"
import {
  ArrowRight,
  ShieldCheck,
  Clock,
  RotateCcw,
  Sparkles,
  CheckCircle2,
  Fingerprint
} from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import { Badge } from "@/core/ui/badge"
import { Button } from "@/core/ui/button"
import { createCheckoutSession } from "@/lib/actions/stripe-actions"

// ----------------------------------------------------------------------
// High-End Minimalist Components
// ----------------------------------------------------------------------

const AuroraBackground = () => {
  return (
    <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none bg-[#050505]">
      {/* Static hardware-composited ambient glows (No keyframe animation during scroll) */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-amber-950/20 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-indigo-950/20 rounded-full blur-[100px] pointer-events-none" />
    </div>
  );
};

const AccessCard = () => {
  const x = useMotionValue(0)
  const y = useMotionValue(0)

  const mouseXSpring = useSpring(x, { stiffness: 300, damping: 20 })
  const mouseYSpring = useSpring(y, { stiffness: 300, damping: 20 })

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["15deg", "-15deg"])
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-15deg", "15deg"])

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const width = rect.width
    const height = rect.height
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top
    const xPct = mouseX / width - 0.5
    const yPct = mouseY / height - 0.5
    x.set(xPct)
    y.set(yPct)
  }

  const handleMouseLeave = () => {
    x.set(0)
    y.set(0)
  }

  const handleCardClick = () => {
    window.open("https://buy.stripe.com/bJe6oJbpe55j5Kccndebu01", "_blank", "noopener,noreferrer")
  }

  return (
    <motion.div
      style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleCardClick}
      className="relative w-full max-w-[340px] h-full min-h-[440px] mx-auto rounded-[28px] p-6 md:p-8 flex flex-col justify-between overflow-visible cursor-pointer group"
    >
      {/* Outer Glow (Transitions to Gold Glow on Hover) */}
      <div className="absolute -inset-4 bg-amber-500/0 rounded-[32px] blur-2xl transition-all duration-700 group-hover:bg-amber-500/20 -z-10" />

      {/* Obsidian Glass Background */}
      <div className="absolute inset-0 bg-[#0a0a0a] border border-white/15 rounded-[28px] shadow-2xl transition-all duration-500 hover:bg-black hover:border-amber-500/40 overflow-hidden">
        
        {/* Subtle background sweeps */}
        <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-bl from-white/5 via-transparent to-amber-500/5 opacity-40 group-hover:opacity-70 transition-opacity duration-500" />
        <div className="absolute -top-24 -left-24 w-48 h-48 bg-amber-500/0 group-hover:bg-amber-500/15 rounded-full blur-3xl transition-all duration-500" />
        <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl" />
        
        {/* Top Highlight border */}
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/30 group-hover:via-amber-400/60 to-transparent transition-all duration-500" />
        
        {/* Luxury Micro-Pattern Background (Dots + Micro Grid) */}
        <div 
          className="absolute inset-0 opacity-[0.18] group-hover:opacity-[0.28] transition-opacity duration-500 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0), linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
            backgroundSize: '16px 16px, 32px 32px, 32px 32px',
            backgroundPosition: 'center center'
          }}
        />
      </div>
      
      {/* Card Content - Top */}
      <div className="relative z-10 flex flex-col items-center text-center mt-1" style={{ transform: "translateZ(40px)" }}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-white/50 mb-1 group-hover:text-amber-200/80 transition-colors duration-500">Dedicated Partnership</p>
        <h3 className="text-2xl font-bold tracking-tight text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">PARTNER PASS</h3>
        
        {/* HERO ACCENT: HM Logo (White by Default -> Pure Gold on Hover) */}
        <div className="my-6 relative group-hover:scale-110 transition-transform duration-500">
          <div className="absolute inset-0 bg-white/0 blur-xl rounded-full transition-all duration-500 group-hover:bg-amber-500/40 group-hover:blur-2xl" />
          <div className="p-3.5 rounded-2xl bg-black/60 border border-white/20 backdrop-blur-md relative shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-all duration-500 group-hover:border-amber-400/80 group-hover:bg-amber-950/40 group-hover:shadow-[0_0_30px_rgba(245,158,11,0.5)] flex items-center justify-center">
            <Image
              src="/hm-logo-transparent.png"
              alt="HM Logo"
              width={44}
              height={44}
              priority
              className="w-11 h-11 object-contain drop-shadow-[0_0_8px_rgba(255,255,255,0.4)] group-hover:drop-shadow-[0_0_20px_rgba(251,191,36,1)] transition-all duration-500 [filter:brightness(0)_invert(1)] group-hover:[filter:brightness(0)_invert(80%)_sepia(80%)_saturate(1500%)_hue-rotate(5deg)_brightness(110%)]"
            />
          </div>
        </div>
        
        <div className="px-4 py-1.5 rounded-full border border-amber-500/30 bg-amber-950/30 backdrop-blur-md text-amber-200 text-xs font-semibold tracking-wider shadow-inner group-hover:border-amber-400/60 transition-all duration-500">
          $3,000 / MO
        </div>
        <p className="text-white/30 text-[10px] mt-3 font-mono tracking-[0.2em] group-hover:text-amber-200/50 transition-colors duration-500">HA-PARTNER</p>
      </div>

      {/* Card Content - Bottom (Platinum EMV Chip & Fingerprint) */}
      <div className="relative z-10 w-full flex justify-between items-end mt-auto pt-4" style={{ transform: "translateZ(20px)" }}>
        {/* Platinum EMV Chip */}
        <div className="w-11 h-9 rounded-md bg-gradient-to-br from-zinc-200 via-zinc-400 to-zinc-500 p-[1px] shadow-sm relative overflow-hidden">
          <div className="absolute inset-[1px] rounded-[5px] bg-gradient-to-tr from-zinc-300 to-zinc-100 opacity-90" />
          {/* Chip lines */}
          <div className="absolute top-1/2 left-0 w-full h-px bg-black/20" />
          <div className="absolute top-0 left-1/2 w-px h-full bg-black/20" />
          <div className="absolute top-1/4 left-0 w-full h-px bg-black/10" />
          <div className="absolute bottom-1/4 left-0 w-full h-px bg-black/10" />
        </div>
        
        <div className="flex flex-col items-end gap-1">
          <Fingerprint className="w-4 h-4 text-white/30 group-hover:text-amber-400/80 transition-colors duration-500" />
          <p className="text-[8px] uppercase tracking-[0.25em] text-white/40 font-semibold group-hover:text-amber-200/60 transition-colors duration-500">Dedicated Access</p>
        </div>
      </div>
    </motion.div>
  )
}

// ----------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------

export default function EngagementModels() {
  const [loading, setLoading] = useState<boolean>(false)

  const handleCheckout = () => {
    try {
      setLoading(true)
      window.open("https://buy.stripe.com/bJe6oJbpe55j5Kccndebu01", "_blank", "noopener,noreferrer")
    } catch (err: unknown) {
      console.error('Checkout redirect error:', err)
      toast.error("Partnership Initialization Failed")
    } finally {
      setLoading(false)
    }
  }

  const features = [
    "Custom AI Agents & Workflows",
    "Full-Stack Web & Mobile Apps",
    "Rapid 48–72h Sprint Cycles",
    "100% Code & IP Ownership",
    "Direct Slack & Async Access",
    "Pause or cancel anytime"
  ];

  return (
    <section id="pricing" className="relative py-20 md:py-24 overflow-hidden flex items-center" style={{ contain: "paint" }}>
      <AuroraBackground />

      <div className="container relative z-10 px-4 md:px-8 mx-auto w-full max-w-6xl">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white">
              One subscription,<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white/80 to-white/40 italic font-serif">endless execution.</span>
            </h2>
          </motion.div>
        </div>

        {/* Both Cards Aligned in Equal-Height Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch max-w-5xl mx-auto">
          
          {/* Left Side: The 3D Access Card */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 flex justify-center h-full perspective-[1200px]"
          >
            <AccessCard />
          </motion.div>

          {/* Right Side: The Pricing Details */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="lg:col-span-7 w-full flex flex-col"
          >
            <div className="rounded-[28px] border border-amber-500/20 bg-[#0a0a0a] shadow-2xl p-6 md:p-8 flex flex-col justify-between h-full relative overflow-hidden">
              
              <div>
                {/* Header */}
                <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-white leading-tight mb-4">
                  The Engineering Partnership
                </h3>

                {/* Subtle Dashed Separator for Breathing Space */}
                <div className="w-full border-b border-dashed border-white/10 mb-6" />

                {/* Promotional Price Display with Emerald SAVE 40% Pill */}
                <div className="mb-6 flex flex-col gap-1">
                  <div className="flex items-center gap-2.5">
                    <span className="text-lg text-white/30 font-medium line-through decoration-amber-500/50 decoration-2">$4,990</span>
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-semibold uppercase tracking-wider">
                      SAVE 40%
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl md:text-6xl font-semibold tracking-tighter text-white drop-shadow-sm">$3,000</span>
                    <span className="text-white/40 text-sm font-medium">/ month</span>
                  </div>
                </div>

                {/* Feature Grid Box */}
                <div className="rounded-[18px] bg-white/[0.02] border border-white/5 p-5 md:p-6 relative mt-4 mb-6">
                  <div className="absolute -top-2.5 left-5 bg-[#0f0f0f] px-2.5 border border-white/5 rounded-full">
                    <span className="text-[9px] uppercase tracking-[0.2em] text-white/60 font-semibold">Included</span>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3.5 pt-1">
                    {features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="shrink-0 bg-cyan-500/10 rounded-full p-0.5">
                          <CheckCircle2 className="w-3 h-3 text-cyan-400" />
                        </div>
                        <span className="text-[11px] md:text-xs font-normal text-white/60 leading-tight">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* CTA Button opening $3,000/mo Promo Stripe Link in New Tab */}
              <a
                href="https://buy.stripe.com/bJe6oJbpe55j5Kccndebu01"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full mt-auto"
              >
                <Button
                  disabled={loading}
                  onClick={handleCheckout}
                  className="w-full h-14 rounded-[14px] font-semibold text-sm transition-all duration-300 bg-white text-black hover:bg-zinc-200 hover:scale-[1.01] shadow-[0_4px_20px_rgba(255,255,255,0.15)] group relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
                  {loading ? (
                    <span className="flex items-center justify-center gap-3 relative z-10 font-medium">
                      <RotateCcw className="h-4 w-4 animate-spin text-black/60" />
                      Initializing...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2 relative z-10">
                      Claim Promotional Offer
                      <ArrowRight className="h-4 w-4 ml-1 opacity-70 group-hover:translate-x-1 transition-transform" />
                    </span>
                  )}
                </Button>
              </a>

            </div>
          </motion.div>
        </div>

        {/* Minimalist Flexibility Indicators */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto border-t border-white/5 pt-8">
          {[
            { icon: <Clock className="w-4 h-4" />, label: "Predictable Delivery" },
            { icon: <RotateCcw className="w-4 h-4" />, label: "Full Flexibility" },
            { icon: <ShieldCheck className="w-4 h-4" />, label: "Impeccable Quality" },
            { icon: <Sparkles className="w-4 h-4" />, label: "Absolute Clarity" }
          ].map((item, i) => (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * i + 0.2, duration: 0.4 }}
              key={i} 
              className="flex flex-col items-center justify-center gap-2 group cursor-default"
            >
              <div className="text-white/20 group-hover:text-cyan-400/80 transition-colors duration-500">
                {item.icon}
              </div>
              <span className="text-[9px] uppercase tracking-widest font-semibold text-white/30 group-hover:text-white/70 transition-colors duration-500">
                {item.label}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
